'use client';

import type { ReactNode } from 'react';
import { Activity, ArrowDown, ArrowUp, ArrowDownAZ, CalendarClock, Coins, Cpu, Layers, Users, X, ChevronDown } from 'lucide-react';
import { interpolate, useTranslation } from '../../lib/i18n';
import { Button } from '../../components/ui/Button';
import { PageFilters, PageFilterChips } from '../../components/ui/PageFilters';
import type { PageFilterField } from 'elowen-plugin-ui-kit';
import { useMobileViewport } from '../../lib/useMobile';
import { RegisterSearch } from '../../components/ui/RegisterSearch';
import { Checkbox } from '../../components/ui/shadcn/checkbox';
import { SelectMenu, type SelectMenuOption } from '../../components/ui/SelectMenu';
import { selectTriggerVariants } from '../../components/ui/shadcn/select';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../components/ui/shadcn/dropdown-menu';
import type {
  ConversationListFilterOption, ConversationListSortKey, ConversationListStatus, ConversationListToolbarProps,
} from './conversationListModel';

function FilterMenu({ label, options, selected, onChange, busy, hint }: {
  label: string;
  options: readonly ConversationListFilterOption[];
  selected: readonly string[];
  onChange: (values: string[]) => void;
  busy: boolean;
  hint?: string;
}) {
  const { t } = useTranslation();
  const triggerLabel = selected.length === 1
    ? `${label}: ${options.find(option => option.value === selected[0])?.label}`
    : selected.length > 1 ? interpolate(t.conversationList.filters.selectedValues, { filter: label, count: selected.length }) : label;
  const selectedOptions = options.filter(option => selected.includes(option.value));
  const valueLabel = selectedOptions.length ? selectedOptions.map(option => option.label).join(', ') : t.managePicker.all;
  return <div className="flex min-w-0 items-center gap-0.5">
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" disabled={busy}
          aria-label={triggerLabel} className={selectTriggerVariants()}>
          <span className="flex shrink-0 text-primary" aria-hidden>{selectedOptions[0]?.icon ?? <Layers size={14} />}</span>
          <span className="min-w-0 flex-1 truncate text-left">{valueLabel}</span>
          <ChevronDown size={12} aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" aria-label={label} className="max-w-[calc(100vw_-_--spacing(8))]">
        {hint ? <DropdownMenuLabel className="max-w-64 whitespace-normal">{hint}</DropdownMenuLabel> : null}
        {options.map(option => <DropdownMenuCheckboxItem key={option.value}
          checked={selected.includes(option.value)} disabled={busy}
          onSelect={event => event.preventDefault()}
          onCheckedChange={checked => onChange(checked ? [...selected, option.value] : selected.filter(value => value !== option.value))}>
          {option.icon}<span className="min-w-0 flex-1 truncate">{option.label}</span>
        </DropdownMenuCheckboxItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
    {selected.length ? <Button type="button" variant="ghost" size="icon" disabled={busy}
      aria-label={interpolate(t.conversationList.filters.clearOne, { filter: label })}
      onClick={() => onChange([])}><X size={12} aria-hidden /></Button> : null}
  </div>;
}

/** The panel owns facet values; the canonical options panel owns disclosure and active chips. */
export function ConversationListToolbar(props: ConversationListToolbarProps) {
  const { t } = useTranslation();
  const phone = useMobileViewport() === true;
  const { filters, options, busy, sort } = props;
  const sortOptions: SelectMenuOption<ConversationListSortKey>[] = [
    { value: 'updated', label: t.conversationList.sort.newest, icon: <CalendarClock size={14} /> },
    { value: 'tokens', label: t.conversationList.sort.tokens, icon: <Coins size={14} /> },
    { value: 'title', label: t.conversationList.sort.name, icon: <ArrowDownAZ size={14} /> },
    { value: 'state', label: t.conversationList.sort.state, icon: <Activity size={14} /> },
    { value: 'model', label: t.conversationList.sort.model, icon: <Cpu size={14} /> },
    ...(props.view === 'all' ? [{ value: 'owner' as const, label: t.conversationList.sort.owner, icon: <Users size={14} /> }] : []),
  ];

  const facetControls: Record<keyof typeof filters, ReactNode> = {
    projects:
      <FilterMenu label={t.conversationList.filters.project} options={options.projects} selected={filters.projects} busy={busy}
        onChange={projects => props.onFilters({ ...filters, projects })} />,
    statuses:
      <FilterMenu label={t.conversationList.filters.status} options={options.statuses} selected={filters.statuses} busy={busy}
        hint={props.view === 'all' ? t.conversationList.filters.unreadScope : undefined}
        onChange={statuses => props.onFilters({ ...filters, statuses: statuses.map(value => {
          const status = options.statuses.find(option => option.value === value)?.value;
          if (status !== 'working' && status !== 'waiting' && status !== 'unread' && status !== 'error') {
            throw new Error(`Unknown conversation status: ${value}`);
          }
          return status satisfies ConversationListStatus;
        }) })} />,
    models:
      <FilterMenu label={t.conversationList.filters.model} options={options.models} selected={filters.models} busy={busy}
        onChange={models => props.onFilters({ ...filters, models })} />,
    types:
      <FilterMenu label={t.conversationList.filters.type} options={options.types} selected={filters.types} busy={busy}
        onChange={types => props.onFilters({ ...filters, types })} />,
    owners:
      props.view === 'all' ? <FilterMenu label={t.conversationList.filters.owner} options={options.owners}
        selected={filters.owners.map(String)} busy={busy}
        onChange={owners => props.onFilters({ ...filters, owners: owners.map(Number) })} /> : null,
  };
  const facetKeys = ['projects', 'statuses', 'models', 'types', ...(props.view === 'all' ? ['owners' as const] : [])] as const;
  const labels = { projects: t.conversationList.filters.project, statuses: t.conversationList.filters.status,
    models: t.conversationList.filters.model, types: t.conversationList.filters.type, owners: t.conversationList.filters.owner };
  const fields: PageFilterField[] = facetKeys.map(key => {
    const values = filters[key].map(String);
    const base = { id: key, label: labels[key], control: facetControls[key] };
    return values.length ? { ...base, active: true, activeLabel: `${labels[key]}: ${values.map(value => options[key].find(option => option.value === value)?.label ?? value).join(', ')}`,
      onReset: () => props.onFilters(current => ({ ...current, [key]: [] })) } : { ...base, active: false };
  });
  if (props.scopeField) {
    const scope = props.scopeField;
    const control = <fieldset disabled={busy} className="contents">{scope.control}</fieldset>;
    fields.unshift(scope.active
      ? { ...scope, control, onReset: () => { if (!busy) scope.onReset(); } }
      : { ...scope, control });
  }
  fields.push({ id: 'sort', label: t.conversationList.sort.label, active: false,
    control: <SelectMenu value={sort.key} label={t.conversationList.sort.label} options={sortOptions} disabled={busy}
      onChange={key => props.onSort({ key, direction: key === 'updated' || key === 'tokens' ? 'desc' : 'asc' })} />,
  }, { id: 'sort-direction', label: t.conversationList.sort.direction, active: false,
    control: <SelectMenu value={sort.direction} label={t.conversationList.sort.direction} disabled={busy}
      options={[
        { value: 'desc', label: t.conversationList.sort.descending, icon: <ArrowDown size={14} /> },
        { value: 'asc', label: t.conversationList.sort.ascending, icon: <ArrowUp size={14} /> },
      ]} onChange={direction => props.onSort({ ...sort, direction })} />,
  });
  return <div className="min-w-0 space-y-2 pb-2">
    <fieldset disabled={busy} data-conversation-toolbar-row className="flex min-w-0 items-center gap-2 border-0 p-0">
      <RegisterSearch value={props.search} onChange={props.onSearch}
        placeholder={t.brainChat.searchPlaceholder} label={t.sessionsPanel.searchLabel} autoFocusInOverlay />
      {props.newConversation}
      <PageFilters fields={fields} iconOnly={phone} named="options" actions={<>
        {!props.selectionMode ? <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={props.onSelectionMode}>{t.conversationList.selectMode}</Button> : null}
        {props.deleteAll}
      </>} />
    </fieldset>
    <PageFilterChips fields={fields} />
    {props.selectionMode ? <label className="flex items-center gap-2 text-small">
      <Checkbox checked={props.pageSelection} onCheckedChange={props.onSelectPage} disabled={busy} aria-label={t.conversationList.selectPage} />
      {t.conversationList.selectPage}
    </label> : null}
  </div>;
}
